import { useEffect } from 'react';
import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'aidu.vendor.theme';

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Re-evaluates resolved theme; called when system preference changes. */
  refreshResolved: () => void;
}

function readStoredMode(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

const initialMode = readStoredMode();

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: initialMode,
  resolved: resolve(initialMode),
  setMode: (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore — non-fatal
    }
    set({ mode, resolved: resolve(mode) });
  },
  refreshResolved: () => set({ resolved: resolve(get().mode) }),
}));

/**
 * Hook that wires the `system` mode to the OS preference change events and
 * keeps the resolved theme in sync.
 */
export function useThemeSync(): void {
  const mode = useThemeStore((s) => s.mode);
  const refresh = useThemeStore((s) => s.refreshResolved);
  useEffect(() => {
    if (mode !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => refresh();
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [mode, refresh]);
}
